import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { services } from '@/services';
import { detachAll as detachAllFirestoreListeners } from '@/services/firebase/listenerRegistry';
import { useCheckBatchStore } from '@/stores/checkBatchStore';
import { useGroupContextStore } from '@/stores/groupContextStore';
import { useProjectAssetsStore } from '@/stores/projectAssetsStore';
import { useWalletStore } from '@/stores/walletStore';
import { isWorkspaceActive } from '@/types';

/**
 * 「工作區不可切入」守門 reject 的 error message(可辨識 identity)。
 *
 * R105 金流移除(docs/64 合約制)後**無 `/billing/*` 頁可導向** — caller catch 到
 * 只需 log / 不切(SUSPENDED 通常已在 caller 端先擋,如 GroupSwitcher 的 disabled row)。
 */
export const WORKSPACE_NOT_SWITCHABLE = 'WORKSPACE_NOT_SWITCHABLE';

/**
 * Switch-group orchestrator(R45 / Beta v0.0.1 Phase 4 → R48.x BE handoff 2026-05-25)
 * -----------------------------------------------------------------------------
 * **R48.x 重要 pivot**:對齊 BE 拍板「切 group → `PATCH /api/me {
 * current_group_id }` → re-fetch `GET /api/me` 拿新 wallet」(BE handoff §5)。
 *
 * 流程:
 *   1. `PATCH /api/me { currentGroupId: newGid }`(BE 同步寫進 profile)
 *   2. 成功 → detach all Firestore listeners(避免 onSnapshot push 寫進舊 group store)
 *   3. clear stale per-group state(walletStore + projectAssetsStore;authStore /
 *      availableGroups 不清。R72 起 nodes/edges/presence store 已隨 Phase 3 死碼清除刪除)
 *   4. setCurrentGroupId(newGid)(L1.5 header 立刻切到新 gid)
 *   5. **re-fetch `GET /api/me`** 拿新 group 的 wallet 寫進 walletStore(BE handoff §5)
 *   6. navigate `/projects`(避免留在舊 group 的 stale URL)
 *
 * **工作區狀態守門**:目標 gid 在 `availableGroups` 內且非 ACTIVE(`SUSPENDED` 停用 /
 * 合約到期,走 `isWorkspaceActive` selector)→ **不打 PATCH /me** 直接 reject
 * `Error(WORKSPACE_NOT_SWITCHABLE)` — 切進非 ACTIVE 工作區消費只會撞 BE 403
 * `WORKSPACE_NOT_ACTIVE`,在入口擋下。目標 gid **不在** `availableGroups` → 不擋
 *(bootstrap / create 後 list 未填等情境維持既有行為,由 BE PATCH /me 二次驗證把關)。
 *
 * **錯誤處理**:
 *   - 目標非 ACTIVE → reject `Error(WORKSPACE_NOT_SWITCHABLE)`(R105:caller 僅 log,
 *     合約制無自助付款頁可導)
 *   - PATCH /me 失敗(422 / 403 gid 不在 user membership)→ 拋 error 給 caller
 *     (caller 端 try/catch + errorMap toast)
 *   - re-fetch /me 失敗(網路 flap)→ console.warn,wallet 留空,`useWalletRealtime`
 *     onSnapshot 會自動推回(walletStore 訂閱新 gid doc)
 *
 * **不重 fetch /groups**:`useGroupBootstrap` dedupe 不會 re-fire;availableGroups
 * 沒變(切的是 currentGroupId,membership 沒動)。
 *
 * @returns `switchGroup(gid)` — async,resolve 時表示 navigate 完成
 * @throws 目標非 ACTIVE → `Error(WORKSPACE_NOT_SWITCHABLE)`;PATCH /me 失敗 → 原 error propagate
 */
export function useSwitchGroup(): (newGid: string) => Promise<void> {
  const navigate = useNavigate();

  return useCallback(
    async (newGid: string) => {
      // 0. R73 billing 不變量守門:目標工作區非 ACTIVE(待付款 / 停用)→ 不打
      //    PATCH /me 直接 reject。用 getState() 讀 call 當下最新 list(callback
      //    identity 不隨 availableGroups 變動);不在 list 內不擋(見 JSDoc)。
      const target = useGroupContextStore
        .getState()
        .availableGroups.find((g) => g.gid === newGid);
      if (target && !isWorkspaceActive(target)) {
        throw new Error(WORKSPACE_NOT_SWITCHABLE);
      }

      // 1. PATCH /me — BE 拍板「同步寫 current_group_id」(BE handoff §5)
      //    失敗(eg. gid 不在 user 的 group 列表)→ 直接 propagate 給 caller(避免
      //    本地 state 跟 BE 不一致 leak 進後續 fetch)
      await services.meService.patch({ currentGroupId: newGid });

      // 2. detach 全部 Firestore listener(避免 onSnapshot push 寫進新 group store
      //    或殘留舊 listener 寫進舊 gid bucket)
      detachAllFirestoreListeners();

      // 3. clear stale per-group state
      useWalletStore.getState().clear();
      useProjectAssetsStore.getState().clearAll();
      // R211 — 批次檢查進度是 per-(user,group)(store JSDoc 已如此宣告):不清的話
      // A 工作區的進度條/minibar 會顯示在 B(badge key 對不上、全暗),使用者按
      // 「取消檢查」實際取消掉的是 A 仍在跑的批次。
      useCheckBatchStore.getState().clearAll();

      // 4. set new gid(L1.5 header 立刻切到新 gid;useWalletRealtime 訂閱新 doc)
      useGroupContextStore.getState().setCurrentGroupId(newGid);

      // 5. re-fetch /me 拿新 group 的 wallet(BE handoff §5「重新 fetch /me 拿新 wallet」)
      //    失敗不擋 navigate — useWalletRealtime onSnapshot 自然推回(BE 已寫
      //    profile.current_group_id,wallets/{gid} doc 訂閱會 fire)
      try {
        const me = await services.meService.get();
        if (me.wallet && me.currentGroupId === newGid) {
          useWalletStore.getState().setWallet(newGid, me.wallet);
        }
      } catch (err) {
        console.warn('[switch-group] failed to re-fetch /me', err);
      }

      // 6. navigate /projects(避免留舊 group `/projects/{stale_pid}` URL)
      navigate('/projects', { replace: true });
    },
    [navigate],
  );
}
